import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCommon } from '@/contexts/common-context';
import { useOrganization } from '@/contexts/organization-context';
import { toast } from '@/hooks/use-toast';
import { approvalClient } from '@/lib/approval-client';
import type { StorePriceCreateRequest, StorePriceCurrent } from '@/types/approval';
import { APPLY_TYPES, LinePreview, hasNoRealApprover, useLinePreview } from './approval-common';
import { orgsOfType, uniqueOils } from './approval-search';
import dayjs from 'dayjs';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 저장 후 상세로 이동시키기 위해 새 요청 id 를 넘긴다 */
  onSubmitted: (requestId: number) => void;
}

/** 판매단가 승인요청 상신(B210). 적용구분 4·B 는 최종승인 후 POS 마감 시 적용된다 */
export function StorePriceDialog({ open, onOpenChange, onSubmitted }: Props) {
  const [orgCd, setOrgCd] = useState('');
  const [productCode, setProductCode] = useState('');
  const [productName, setProductName] = useState('');
  const [applyType, setApplyType] = useState('4');
  const [applyDate, setApplyDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [applyRound, setApplyRound] = useState('1');
  const [current, setCurrent] = useState<StorePriceCurrent | null>(null);
  const [currentFailed, setCurrentFailed] = useState(false);
  const [requestedPrice, setRequestedPrice] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [saving, setSaving] = useState(false);

  // 결재 SPA 는 메인 쉘 밖이라 조직 목록을 아무도 안 불러 준다
  const { organizations, loadOrganizations } = useOrganization();
  const { getOils } = useCommon();
  useEffect(() => {
    if (open && organizations.length === 0) loadOrganizations();
  }, [open, organizations.length, loadOrganizations]);
  const stores = useMemo(() => orgsOfType(organizations, 'STORE'), [organizations]);
  const oils = useMemo(() => uniqueOils(getOils(orgCd)), [getOils, orgCd]);

  /**
   * 현재 적용 중인 단가는 요청자가 외워 적을 값이 아니다. 매장·유종·적용구분이 정해지면 마스터에서 읽어 보여 준다.
   * 조합이 바뀌면 이전 값부터 버린다 - 응답이 오기 전에 상신하면 이전 유종의 단가가 그대로 저장된다.
   * 실패를 조용히 넘기면 안 된다: currentPrice 가 null 이면 서버 조건 엔진이 PRICE_GAP_AMT·VARIANCE_RATE 를
   * 미평가로 보고 조건 없는 기본(짧은) 결재선을 택한다. 수기 입력칸도 없앴으니 요청자가 복구할 수단이 없다.
   */
  useEffect(() => {
    setCurrent(null);
    setCurrentFailed(false);
    if (!orgCd || !productCode) return;
    let cancelled = false;
    approvalClient
      .currentStorePrice(orgCd, productCode, applyType)
      .then(found => !cancelled && setCurrent(found))
      .catch(() => !cancelled && setCurrentFailed(true));
    return () => {
      cancelled = true;
    };
  }, [orgCd, productCode, applyType]);

  useEffect(() => {
    if (!open) return;
    setOrgCd('');
    setProductCode('');
    setProductName('');
    setApplyType('4');
    setApplyDate(dayjs().format('YYYY-MM-DD'));
    setApplyRound('1');
    setRequestedPrice('');
    setRequestReason('');
  }, [open]);

  // 어느 칸이 비었는지 그대로 알려준다 - "필수입니다" 한 줄이면 다 채웠다고 믿는 칸을 찾을 수가 없다
  const missing = (
    [
      ['사업장', orgCd],
      ['유종', productCode],
      ['승인요청단가', requestedPrice],
      ['승인요청사유', requestReason],
    ] as const
  )
    .filter(([, value]) => !value.trim())
    .map(([label]) => label);
  const ready = missing.length === 0;
  // 미리보기와 상신이 같은 본문을 보내야 같은 결재선이 나온다
  const body = (): StorePriceCreateRequest => ({
    orgCd: orgCd.trim(),
    productCode: productCode.trim(),
    productName: productName.trim() || null,
    applyType,
    applyTypeName: APPLY_TYPES.find(t => t.code === applyType)?.name ?? null,
    applyDate,
    applyRound: Number(applyRound) || 1,
    currentPrice: current?.price ?? null,
    requestedPrice: Number(requestedPrice),
    requestReason: requestReason.trim(),
  });

  const preview = useLinePreview('store-price', ready && !currentFailed ? body() : null);
  const blocked = currentFailed || hasNoRealApprover(preview.data);

  const handleSubmit = async () => {
    if (!ready) {
      toast({ title: '입력 확인', description: `비어 있는 항목: ${missing.join(', ')}`, variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const detail = await approvalClient.submitStorePrice(body());
      toast({ title: '상신 완료', description: detail.request.documentNo });
      onOpenChange(false);
      onSubmitted(detail.request.id);
    } catch (error) {
      toast({
        title: '상신 실패',
        description: error instanceof Error ? error.message : '승인요청을 등록하지 못했습니다',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[560px]'>
        <DialogHeader>
          <DialogTitle>판매단가 승인요청</DialogTitle>
          <DialogDescription>
            등록이 곧 상신입니다. 같은 사업장·유종·적용구분에 진행 중인 요청이 있으면 등록되지 않습니다.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-3 py-2'>
          <div className='grid grid-cols-2 gap-3'>
            <div className='space-y-1'>
              <Label>사업장</Label>
              <Select
                value={orgCd}
                onValueChange={code => {
                  setOrgCd(code);
                  // 유종은 주유소마다 다른 상품일 수 있어 이전 사업장의 유종이 남으면 안 된다
                  setProductCode('');
                  setProductName('');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder='사업장 선택' />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(store => (
                    <SelectItem key={store.orgCd} value={store.orgCd!}>
                      {store.orgName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1'>
              <Label>유종</Label>
              {/* 코드와 이름을 따로 받으면 둘이 어긋난다. 마스터에서 고른 한 건이 둘 다 채운다 */}
              <Select
                value={productCode}
                disabled={orgCd === ''}
                onValueChange={code => {
                  setProductCode(code);
                  setProductName(oils.find(oil => oil.code === code)?.name ?? '');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={orgCd === '' ? '사업장을 먼저 고르세요' : '유종 선택'} />
                </SelectTrigger>
                <SelectContent>
                  {oils.map(oil => (
                    <SelectItem key={oil.code} value={oil.code}>
                      {oil.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='space-y-1'>
            <Label>적용구분</Label>
            <Select value={applyType} onValueChange={setApplyType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {APPLY_TYPES.map(t => (
                  <SelectItem key={t.code} value={t.code}>
                    {t.code}. {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 지금 적용 중인 값. 고를 것이 아니라 보고 판단하는 값이라 읽기 전용이다 */}
          <div className='grid grid-cols-3 gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm'>
            <div>
              <div className='text-xs text-muted-foreground'>현재일</div>
              <div className='tabular-nums'>{current?.applyDate ?? '-'}</div>
            </div>
            <div>
              <div className='text-xs text-muted-foreground'>현재차수</div>
              <div className='tabular-nums'>{current?.applyRound ?? '-'}</div>
            </div>
            <div>
              <div className='text-xs text-muted-foreground'>현재단가</div>
              <div className={`font-medium tabular-nums ${currentFailed ? 'text-destructive' : ''}`}>
                {currentFailed ? '조회 실패' : current ? current.price.toLocaleString() : '-'}
              </div>
            </div>
          </div>
          {currentFailed && (
            <p className='text-xs text-destructive'>
              현재 적용단가를 읽지 못했습니다. 이대로 상신하면 결재선이 잘못 잡힐 수 있어 막습니다 - 사업장·유종을 다시 선택해 주세요
            </p>
          )}

          <div className='grid grid-cols-3 gap-3'>
            <div className='space-y-1'>
              <Label htmlFor='sp-date'>적용일</Label>
              <Input id='sp-date' type='date' value={applyDate} onChange={e => setApplyDate(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <Label htmlFor='sp-round'>적용차수</Label>
              <Input id='sp-round' type='number' min={1} max={99} value={applyRound} onChange={e => setApplyRound(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <Label htmlFor='sp-price'>승인요청단가</Label>
              <Input id='sp-price' type='number' value={requestedPrice} onChange={e => setRequestedPrice(e.target.value)} />
            </div>
          </div>

          <div className='space-y-1'>
            <Label htmlFor='sp-reason'>승인요청사유</Label>
            <Textarea id='sp-reason' rows={3} value={requestReason} onChange={e => setRequestReason(e.target.value)} maxLength={500} />
          </div>

          <LinePreview body={ready && !currentFailed ? body() : null} preview={preview} />
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
            취소
          </Button>
          <Button onClick={handleSubmit} disabled={saving || blocked}>
            {saving ? '상신 중...' : '상신'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
